// Copyright (c) Ping. All rights reserved.

import React, { useCallback } from 'react';
import {
  Pressable,
  StyleSheet,
  Text,
  TextInput,
  type TextStyle,
  View,
  type ViewStyle,
} from 'react-native';
import { useColorScheme } from '../../foundations/colorScheme';
import { createTheme } from '../../design-system/theme';
import { tasksStrings } from './strings';

export interface TasksSearchBarProps {
  readonly value: string;
  readonly onChange: (next: string) => void;
}

/**
 * Search input. The view-model debounces the upstream commit; this
 * component is purely presentation.
 */
export const TasksSearchBar = React.memo<TasksSearchBarProps>(({ value, onChange }) => {
  const scheme = useColorScheme();
  const theme = createTheme(scheme === 'dark' ? 'dark' : 'light');

  const handleClear = useCallback(() => {
    onChange('');
  }, [onChange]);

  return (
    <View
      style={[
        styles.container,
        {
          backgroundColor: theme.colors.surface.secondary,
          borderColor: theme.colors.border.secondary,
        },
      ]}
    >
      <TextInput
        value={value}
        onChangeText={onChange}
        placeholder={tasksStrings.searchPlaceholder}
        placeholderTextColor={theme.colors.text.tertiary}
        accessibilityLabel={tasksStrings.searchPlaceholder}
        autoCorrect={false}
        autoCapitalize="none"
        style={[styles.input, { color: theme.colors.text.primary }]}
      />
      {value.length > 0 ? (
        <Pressable
          onPress={handleClear}
          accessibilityRole="button"
          accessibilityLabel={tasksStrings.searchClearLabel}
          hitSlop={8}
        >
          <Text style={[styles.clearText, { color: theme.colors.text.secondary }]}>×</Text>
        </Pressable>
      ) : null}
    </View>
  );
});

TasksSearchBar.displayName = 'TasksSearchBar';

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 8,
    borderWidth: 1,
    marginHorizontal: 16,
    marginVertical: 8,
  } satisfies ViewStyle,
  input: {
    flex: 1,
    fontSize: 14,
    padding: 0,
  } satisfies TextStyle,
  clearText: {
    fontSize: 18,
    fontWeight: '600',
    paddingHorizontal: 4,
  } satisfies TextStyle,
});
